import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { Button } from 'components/Button';
import { StyledTextField } from 'components/Input/styles';

import { RISK_MATRIX_CONSTANTS } from '../../risk-assessments-node/constants';
import { COLUMN_TYPE_LABELS, MAX_COLUMN_NAME_LENGTH, MAX_MATRIX_LABEL_LENGTH } from '../constants';
import { generateColumnId } from '../utils';
import {
  StyledAddColumnRow,
  StyledColumnActions,
  StyledColumnRow,
  StyledColumnTypeSelect,
  StyledColumnsGrid,
  StyledConfigDialogContent,
  StyledLabelIndex,
  StyledLabelRow,
  StyledLabelsColumn,
  StyledLabelsList,
  StyledLabelsRow,
  StyledMatrixSizeCloseIcon,
  StyledMatrixSizeCloseIconWrapper,
  StyledMatrixSizeField,
  StyledMatrixSizeLabel,
  StyledMatrixSizeRow,
  StyledSectionDescription,
  StyledSectionTitle,
} from '../styles';
import type { Column, ColumnType, RiskAssessmentConfigDialogProps } from '../types';

export const RiskAssessmentConfigDialog: React.FC<RiskAssessmentConfigDialogProps> = ({
  open,
  configData,
  onConfigDataChange,
  onSave,
  onCancel,
}) => {
  const titleId = React.useId();

  const handleColumnLabelChange = (index: number, label: string): void => {
    const newColumns = [...configData.columns];
    newColumns[index] = { ...newColumns[index], label };
    onConfigDataChange({ ...configData, columns: newColumns });
  };

  const handleColumnTypeChange = (index: number, type: ColumnType): void => {
    const newColumns = [...configData.columns];
    newColumns[index] = { ...newColumns[index], type };
    onConfigDataChange({ ...configData, columns: newColumns });
  };

  const handleColumnDelete = (index: number): void => {
    onConfigDataChange({
      ...configData,
      columns: configData.columns.filter((_, i) => i !== index),
    });
  };

  const handleColumnMove = (index: number, direction: -1 | 1): void => {
    const target = index + direction;
    if (target < 0 || target >= configData.columns.length) return;
    const newColumns = [...configData.columns];
    [newColumns[index], newColumns[target]] = [newColumns[target], newColumns[index]];
    onConfigDataChange({ ...configData, columns: newColumns });
  };

  const handleAddColumn = (): void => {
    const newColumn: Column = { id: generateColumnId(), label: 'New column', type: 'text' };
    onConfigDataChange({ ...configData, columns: [...configData.columns, newColumn] });
  };

  const handleLabelChange = (
    target: 'likelihoodLabels' | 'severityLabels',
    index: number,
    value: string
  ): void => {
    const newLabels = [...configData[target]];
    newLabels[index] = value;
    onConfigDataChange({ ...configData, [target]: newLabels });
  };

  const handleMatrixSizeChange = (
    target: 'likelihoodLabels' | 'severityLabels',
    nextValue: number
  ): void => {
    const clamped = Math.max(
      RISK_MATRIX_CONSTANTS.MIN_SIZE,
      Math.min(RISK_MATRIX_CONSTANTS.MAX_SIZE, nextValue)
    );
    const next = [...configData[target]];
    while (next.length < clamped) next.push(`Level ${next.length + 1}`);
    while (next.length > clamped) next.pop();
    onConfigDataChange({ ...configData, [target]: next });
  };

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth="md"
      // Disable MUI's auto-focus + scroll-lock + focus restoration: this dialog is opened
      // from inside a Tiptap contenteditable, and MUI's focus dance steals selection on open
      // and scrolls the parent doc on close. Re-evaluate if a11y review pushes back.
      disableAutoFocus
      disableScrollLock
      keepMounted={false}
      disableRestoreFocus
      onClose={onCancel}
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId}>Configure Risk Assessment</DialogTitle>
      <DialogContent>
        <StyledConfigDialogContent>
          <div>
            <StyledSectionTitle>Columns</StyledSectionTitle>
            <StyledSectionDescription>
              Add, remove, rename, or reorder columns and choose each column type.
            </StyledSectionDescription>
            <StyledColumnsGrid>
              {configData.columns.map((col, index) => (
                <StyledColumnRow key={col.id}>
                  <StyledTextField
                    variant="outlined"
                    size="small"
                    value={col.label}
                    onChange={(e) => handleColumnLabelChange(index, e.target.value)}
                    inputProps={{ maxLength: MAX_COLUMN_NAME_LENGTH }}
                  />
                  <StyledColumnTypeSelect
                    variant="outlined"
                    size="small"
                    value={col.type}
                    onChange={(e: SelectChangeEvent<unknown>) =>
                      handleColumnTypeChange(index, e.target.value as ColumnType)
                    }
                  >
                    {(Object.keys(COLUMN_TYPE_LABELS) as Array<keyof typeof COLUMN_TYPE_LABELS>).map(
                      (typeKey) => (
                        <MenuItem key={typeKey} value={typeKey}>
                          {COLUMN_TYPE_LABELS[typeKey]}
                        </MenuItem>
                      )
                    )}
                  </StyledColumnTypeSelect>
                  <StyledColumnActions>
                    <IconButton
                      size="small"
                      onClick={() => handleColumnMove(index, -1)}
                      disabled={index === 0}
                      aria-label="Move column up"
                    >
                      <ArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleColumnMove(index, 1)}
                      disabled={index === configData.columns.length - 1}
                      aria-label="Move column down"
                    >
                      <ArrowDownIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleColumnDelete(index)}
                      aria-label="Delete column"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </StyledColumnActions>
                </StyledColumnRow>
              ))}
            </StyledColumnsGrid>
            <StyledAddColumnRow>
              <Button variant="outlined" size="small" onClick={handleAddColumn}>
                + Add column
              </Button>
            </StyledAddColumnRow>
          </div>

          <div>
            <StyledSectionTitle>Risk matrix size</StyledSectionTitle>
            <StyledSectionDescription>
              Controls the matrix shown when setting Risk rating on a row. Allowed range:{' '}
              {RISK_MATRIX_CONSTANTS.MIN_SIZE}–{RISK_MATRIX_CONSTANTS.MAX_SIZE}.
            </StyledSectionDescription>
            <StyledMatrixSizeRow>
              <div>
                <StyledMatrixSizeLabel>Likelihood</StyledMatrixSizeLabel>
                <StyledMatrixSizeField
                  variant="outlined"
                  size="small"
                  type="number"
                  value={configData.likelihoodLabels.length}
                  onChange={(e) =>
                    handleMatrixSizeChange(
                      'likelihoodLabels',
                      parseInt(e.target.value, 10) || RISK_MATRIX_CONSTANTS.DEFAULT_COLUMNS
                    )
                  }
                  inputProps={{
                    min: RISK_MATRIX_CONSTANTS.MIN_SIZE,
                    max: RISK_MATRIX_CONSTANTS.MAX_SIZE,
                  }}
                />
              </div>
              <StyledMatrixSizeCloseIconWrapper>
                <StyledMatrixSizeCloseIcon />
              </StyledMatrixSizeCloseIconWrapper>
              <div>
                <StyledMatrixSizeLabel>Severity</StyledMatrixSizeLabel>
                <StyledMatrixSizeField
                  variant="outlined"
                  size="small"
                  type="number"
                  value={configData.severityLabels.length}
                  onChange={(e) =>
                    handleMatrixSizeChange(
                      'severityLabels',
                      parseInt(e.target.value, 10) || RISK_MATRIX_CONSTANTS.DEFAULT_ROWS
                    )
                  }
                  inputProps={{
                    min: RISK_MATRIX_CONSTANTS.MIN_SIZE,
                    max: RISK_MATRIX_CONSTANTS.MAX_SIZE,
                  }}
                />
              </div>
            </StyledMatrixSizeRow>
          </div>

          <StyledLabelsRow>
            <StyledLabelsColumn>
              <StyledSectionTitle>Likelihood labels</StyledSectionTitle>
              <StyledSectionDescription $tight>
                Shown on the likelihood axis of the matrix.
              </StyledSectionDescription>
              <StyledLabelsList>
                {configData.likelihoodLabels.map((label, index) => (
                  <StyledLabelRow key={`l-${index}`}>
                    <StyledLabelIndex>{index + 1}</StyledLabelIndex>
                    <StyledTextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={label}
                      onChange={(e) => handleLabelChange('likelihoodLabels', index, e.target.value)}
                      inputProps={{ maxLength: MAX_MATRIX_LABEL_LENGTH }}
                    />
                  </StyledLabelRow>
                ))}
              </StyledLabelsList>
            </StyledLabelsColumn>
            <StyledLabelsColumn>
              <StyledSectionTitle>Severity labels</StyledSectionTitle>
              <StyledSectionDescription $tight>
                Shown on the severity axis of the matrix.
              </StyledSectionDescription>
              <StyledLabelsList>
                {configData.severityLabels.map((label, index) => (
                  <StyledLabelRow key={`s-${index}`}>
                    <StyledLabelIndex>{index + 1}</StyledLabelIndex>
                    <StyledTextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={label}
                      onChange={(e) => handleLabelChange('severityLabels', index, e.target.value)}
                      inputProps={{ maxLength: MAX_MATRIX_LABEL_LENGTH }}
                    />
                  </StyledLabelRow>
                ))}
              </StyledLabelsList>
            </StyledLabelsColumn>
          </StyledLabelsRow>
        </StyledConfigDialogContent>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave}>
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};
